/**
 * AuditTrail — per-review event log panel (F10).
 *
 * Reads `document_review_audit` (via /api/document-reviews/{id}/audit) and
 * renders a flat, scrollable timeline of events for the document review:
 * created, sent, opened, annotated, approved, uploaded, confirmed, locked,
 * downloaded — each with actor email (when available) and timestamp.
 *
 * Read-only. No mutations. Used as a side panel in the admin viewer; can
 * also stand alone in a route if needed.
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2, Clock } from 'lucide-react';
import { api } from '@/lib/api';

interface AuditTrailProps {
  reviewId: string;
  /** Optional className passthrough for layout sizing. */
  className?: string;
}

/** Map of raw event code -> human-readable label for the timeline. */
const EVENT_LABELS: Record<string, string> = {
  'review.created': 'Review created',
  'review.sent': 'Sent for review',
  'review.opened': 'Opened by recipient',
  'review.status_changed': 'Status changed',
  'review.cancelled': 'Cancelled',
  'annotation.created': 'Annotation added',
  'annotation.deleted': 'Annotation removed',
  'consent_shown': 'Consent text displayed',
  'signer.signed': 'Signer signed',
  'signature.placed': 'Signature placed',
  'external_stamp.uploaded': 'Externally-stamped PDF uploaded',
  'external_stamp.confirmed': 'External stamp confirmed',
  'review.locked': 'Locked (audit cert generated)',
  'file.downloaded': 'PDF downloaded',
};

function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function actorSummary(actorId: string | null | undefined, details: any): string {
  // Prefer explicit actor email when audit details captured it
  const detailEmail = details?.actor_email || details?.email || details?.signer_email;
  if (detailEmail) return detailEmail;
  if (actorId) return actorId;
  return 'system';
}

export function AuditTrail({ reviewId, className }: AuditTrailProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['document-review-audit', reviewId],
    queryFn: () => api.documentReviews.auditEvents(reviewId),
    enabled: !!reviewId,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 text-sm text-muted-foreground p-4 ${className ?? ''}`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading audit trail…
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-sm text-destructive p-4 ${className ?? ''}`}>
        Failed to load audit trail.
      </div>
    );
  }

  const events = data?.events ?? [];

  if (events.length === 0) {
    return (
      <div className={`text-sm text-muted-foreground p-4 ${className ?? ''}`}>
        No audit events recorded yet.
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className ?? ''}`} data-testid="audit-trail">
      <div className="px-4 py-2 border-b text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/30">
        Audit Trail ({events.length})
      </div>
      <ol className="flex-1 overflow-y-auto divide-y">
        {events.map((ev) => (
          <li
            key={ev.id}
            className="px-4 py-2.5 text-sm"
            data-testid={`audit-event-${ev.event}`}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="font-medium">{eventLabel(ev.event)}</span>
            </div>
            <div className="mt-1 ml-5 text-xs text-muted-foreground space-y-0.5">
              <div>{formatTime(ev.created_at)}</div>
              <div>by {actorSummary(ev.actor_id, ev.details)}</div>
              {ev.ip && <div className="font-mono">{ev.ip}</div>}
              {ev.details && Object.keys(ev.details).length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[10px] uppercase tracking-wider hover:text-foreground">
                    Details
                  </summary>
                  <pre className="mt-1 text-[10px] font-mono whitespace-pre-wrap break-all rounded bg-muted/40 p-2">
                    {JSON.stringify(ev.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
